import { Dialog, Transition } from '@headlessui/react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Fragment, useState } from 'react';
import * as Yup from 'yup';

import { updateMemberRole } from '../../services/workspaceSettingsService';
import { getPermittedRoleOptions } from '../../utils/roleUtils';

import { useAppSelector } from '@store/hooks';

interface Props {
  userId: string;
  currentRole: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const schema = Yup.object({
  applicationRole: Yup.string().required('Please select a role.'),
});

const EditRoleModal: React.FC<Props> = ({
  userId,
  currentRole,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const workspaceRoles: string[] = useAppSelector(
    (s: any) => s.auth?.user?.workspaceContext?.roles ?? []
  );

  const roleOptions = getPermittedRoleOptions(workspaceRoles);

  const [serverError, setServerError] = useState<string | null>(null);

  const handleSubmit = async (
    values: { applicationRole: string },
    { setSubmitting }: any
  ) => {
    setServerError(null);
    try {
      await updateMemberRole(userId, values.applicationRole);
      onSuccess();
      onClose();
    } catch (err: any) {
      if (err?.status === 403 && err?.code === 'ROLE_NOT_PERMITTED') {
        setServerError('You do not have permission to assign this role.');
      } else if (err?.status === 409 && err?.code === 'MEMBERSHIP_NOT_ACTIVE') {
        setServerError("This user's membership is no longer active.");
      } else {
        setServerError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-sm bg-white rounded-lg shadow-xl p-6">
              <Dialog.Title className="text-base font-semibold text-gray-900 mb-4">
                Edit Role
              </Dialog.Title>

              <Formik
                initialValues={{ applicationRole: currentRole }}
                validationSchema={schema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ isSubmitting }) => (
                  <Form className="space-y-4">
                    <div>
                      <label
                        htmlFor="applicationRole"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Application Role
                      </label>
                      <Field
                        as="select"
                        id="applicationRole"
                        name="applicationRole"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {roleOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="applicationRole"
                        component="p"
                        className="mt-1 text-xs text-red-600"
                      />
                    </div>

                    {serverError && (
                      <p className="text-sm text-red-600">{serverError}</p>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {isSubmitting ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditRoleModal;
